import type { Academy } from '@/dto'

import type { PayStatusAuthCheckerFn, RoutePermissionType } from './RoutePermission.type'

export class RoutePermission {
  private _routePermissionByPayStatus: RoutePermissionType

  get permissionByStatus() {
    return this._routePermissionByPayStatus
  }

  constructor(
    userAuthority: res.Teacher['type'],
    payStatus: Academy['payStatus'],
    isSchoolflat: boolean,
    isSeoltab: boolean,
    isFranchise: boolean,
  ) {
    if (this._checkPermission(userAuthority, 'ROOT_TEACHER')) {
      this._routePermissionByPayStatus = this._ROOT_TEACHER(
        payStatus,
        isSchoolflat,
        isSeoltab,
        isFranchise,
      )
    } else if (this._checkPermission(userAuthority, 'HOMEROOM_TEACHER')) {
      this._routePermissionByPayStatus = this._HOMEROOM_TEACHER(
        payStatus,
        isSchoolflat,
        isSeoltab,
        isFranchise,
      )
    } else {
      this._routePermissionByPayStatus = this._MANAGER_TEACHER(
        payStatus,
        isSchoolflat,
        isSeoltab,
        isFranchise,
      )
    }
  }

  // 관리자 선생님
  private _ROOT_TEACHER: PayStatusAuthCheckerFn = (
    payStatus,
    isSchoolflat,
    isSeoltab,
    isFranchise,
  ) => {
    if (isSeoltab) {
      return seoltabRoutePermission
    }
    return {
      myinfo: {
        access: true,
        기관정보: { access: true },
        사이언스플랫: { access: true },
        잉글리시플랫: { access: true },
        이용현황: { access: payStatus !== '무료체험' && !isSchoolflat },
        교재비정산: { access: !isSchoolflat },
        사용가이드: { access: true },
        교재구매내역: { access: true },
      },
      manage: {
        access: true,
        student: {
          등록: true,
          정보수정: true,
          삭제: true,
        },
        class: {
          access: true,
          myClassOnly: false,
          등록: true,
          삭제: true,
        },
        teacher: {
          access: true,
          등록: true,
          정보수정: true,
        },
        sms: {
          access: payStatus !== '무료체험',
          보내기: payStatus !== '무료체험',
          내역: payStatus !== '무료체험',
        },
        studentApp: {
          access: true,
        },
        parentApp: {
          access: true,
        },
        attendance: {
          access: !isSchoolflat,
        },
        tuition: {
          access: true,
        },
      },
      고객지원: {
        access: !isSchoolflat,
      },
      VOD_칼럼: {
        access: !isSchoolflat,
      },
      lesson: {
        access: true,
      },
      prepare: {
        access: true,
        // 학습지교재삭제: true,
        // 학습지교재수정: true,
        // 학습지교재이동: true,
        // 학습지교재출제: true,
        // 시그니처_교재_주문하기: payStatus !== '무료체험',
        학습지만들기: {
          학교별기출: { access: !isSchoolflat },
          기출업로드: { access: !isSchoolflat },
        },
        교재만들기: {
          학교별기출: { access: !isSchoolflat },
        },
        학교별기출: {
          access: !isSchoolflat,
        },
        테마별학습지: {
          access: true,
          내신관: {
            access: true,
            내신대비교과서: {
              access: true,
            },
            내신대비추천: {
              access: true,
            },
            내신대비서술형: {
              access: !isSchoolflat,
            },
          },
          수능경시관: {
            access: true,
            수능모의고사: {
              access: true,
            },
            모의고사쌍둥이: {
              access: true,
            },
            수능특강수능완성: {
              access: !isSchoolflat,
            },
            경찰대사관학교: {
              access: true,
            },
            경찰대사관학교쌍둥이: {
              access: true,
            },
            KMM: {
              access: !isSchoolflat,
            },
          },
          클리닉관: {
            access: true,
          },
          평가관: {
            access: true,
          },
        },
        학습지: {
          access: true,
          내학습지: {
            access: true,
          },
          나의DB: {
            access: true,
          },
          즐겨찾는문제: {
            access: true,
          },
          학습지휴지통: {
            access: true,
          },
        },
        내교재: {
          access: true,
        },
        강의: {
          access: true,
        },
        KMM: {
          access: !isSchoolflat,
        },
        와이즈만: {
          access: isFranchise,
        },
        다른기관학습지: {
          access: !isSchoolflat,
        },
      },
      schoolExamZone: {
        access: !isSchoolflat,
      },
      챗봇: {
        access: !isSchoolflat,
      },
      orderManage: {
        access: true,
      },
      notification: {
        access: true,
      },
    }
  }
  // 전체 권한 선생님
  private _MANAGER_TEACHER: PayStatusAuthCheckerFn = (
    payStatus,
    isSchoolflat,
    isSeoltab,
    isFranchise,
  ) => {
    if (isSeoltab) {
      return seoltabRoutePermission
    }
    return {
      myinfo: {
        access: true,
        기관정보: { access: true },
        사이언스플랫: { access: true },
        잉글리시플랫: { access: true },
        이용현황: { access: false },
        교재비정산: { access: false },
        사용가이드: { access: true },
        교재구매내역: { access: true },
      },
      manage: {
        access: true,
        student: {
          등록: true,
          정보수정: true,
          삭제: true,
        },
        class: {
          access: true,
          myClassOnly: false,
          등록: true,
          삭제: true,
        },
        teacher: {
          access: false,
          등록: false,
          정보수정: false,
        },
        sms: {
          access: payStatus !== '무료체험',
          보내기: payStatus !== '무료체험',
          내역: false,
        },
        studentApp: {
          access: true,
        },
        parentApp: {
          access: true,
        },
        attendance: {
          access: !isSchoolflat,
        },
        tuition: {
          access: true,
        },
      },
      고객지원: {
        access: !isSchoolflat,
      },
      VOD_칼럼: {
        access: !isSchoolflat,
      },
      lesson: {
        access: true,
      },
      prepare: {
        access: true,
        // 학습지교재삭제: true,
        // 학습지교재수정: true,
        // 학습지교재이동: true,
        // 학습지교재출제: true,
        // 시그니처_교재_주문하기: payStatus !== '무료체험',
        학습지만들기: {
          학교별기출: { access: !isSchoolflat },
          기출업로드: { access: !isSchoolflat },
        },
        교재만들기: {
          학교별기출: { access: !isSchoolflat },
        },
        학교별기출: {
          access: !isSchoolflat,
        },
        테마별학습지: {
          access: true,
          내신관: {
            access: true,
            내신대비교과서: {
              access: true,
            },
            내신대비추천: {
              access: true,
            },
            내신대비서술형: {
              access: !isSchoolflat,
            },
          },
          수능경시관: {
            access: true,
            수능모의고사: {
              access: true,
            },
            모의고사쌍둥이: {
              access: true,
            },
            수능특강수능완성: {
              access: !isSchoolflat,
            },
            경찰대사관학교: {
              access: true,
            },
            경찰대사관학교쌍둥이: {
              access: true,
            },
            KMM: {
              access: !isSchoolflat,
            },
          },
          클리닉관: {
            access: true,
          },
          평가관: {
            access: true,
          },
        },
        학습지: {
          access: true,
          내학습지: {
            access: true,
          },
          나의DB: {
            access: true,
          },
          즐겨찾는문제: {
            access: true,
          },
          학습지휴지통: {
            access: true,
          },
        },
        내교재: {
          access: true,
        },
        강의: {
          access: true,
        },
        KMM: {
          access: !isSchoolflat,
        },
        와이즈만: {
          access: isFranchise,
        },
        다른기관학습지: {
          access: !isSchoolflat,
        },
      },
      schoolExamZone: {
        access: !isSchoolflat,
      },
      챗봇: {
        access: !isSchoolflat,
      },
      orderManage: {
        access: true,
      },
      notification: {
        access: true,
      },
    }
  }
  // 담당 권한 선생님
  private _HOMEROOM_TEACHER: PayStatusAuthCheckerFn = (
    payStatus,
    isSchoolflat,
    isSeoltab,
    isFranchise,
  ) => {
    if (isSeoltab) {
      return seoltabRoutePermission
    }
    return {
      myinfo: {
        access: true,
        기관정보: { access: true },
        사이언스플랫: { access: true },
        잉글리시플랫: { access: true },
        이용현황: { access: false },
        교재비정산: { access: false },
        사용가이드: { access: true },
        교재구매내역: { access: true },
      },
      manage: {
        access: true,
        student: {
          등록: false,
          정보수정: true,
          삭제: false,
        },
        class: {
          access: true,
          myClassOnly: true,
          등록: false,
          삭제: false,
        },
        teacher: {
          access: false,
          등록: false,
          정보수정: false,
        },
        sms: {
          access: payStatus !== '무료체험',
          보내기: payStatus !== '무료체험',
          내역: false,
        },
        studentApp: {
          access: true,
        },
        parentApp: {
          access: true,
        },
        attendance: {
          access: !isSchoolflat,
        },
        tuition: {
          access: true,
        },
      },

      고객지원: {
        access: !isSchoolflat,
      },
      VOD_칼럼: {
        access: !isSchoolflat,
      },
      lesson: {
        access: true,
      },
      prepare: {
        access: true,
        // 학습지교재삭제: true,
        // 학습지교재수정: true,
        // 학습지교재이동: true,
        // 학습지교재출제: true,
        // 시그니처_교재_주문하기: payStatus !== '무료체험',
        학습지만들기: {
          학교별기출: { access: !isSchoolflat },
          기출업로드: { access: !isSchoolflat },
        },
        교재만들기: {
          학교별기출: { access: !isSchoolflat },
        },
        학교별기출: {
          access: !isSchoolflat,
        },
        학습지: {
          access: true,
          내학습지: {
            access: true,
          },
          나의DB: {
            access: true,
          },
          즐겨찾는문제: {
            access: true,
          },
          학습지휴지통: {
            access: true,
          },
        },
        내교재: {
          access: true,
        },
        강의: {
          access: true,
        },
        KMM: {
          access: !isSchoolflat,
        },
        와이즈만: {
          access: isFranchise,
        },
        테마별학습지: {
          access: true,
          내신관: {
            access: true,
            내신대비교과서: {
              access: true,
            },
            내신대비추천: {
              access: true,
            },
            내신대비서술형: {
              access: !isSchoolflat,
            },
          },
          수능경시관: {
            access: true,
            수능모의고사: {
              access: true,
            },
            모의고사쌍둥이: {
              access: true,
            },
            수능특강수능완성: {
              access: !isSchoolflat,
            },
            경찰대사관학교: {
              access: true,
            },
            경찰대사관학교쌍둥이: {
              access: true,
            },
            KMM: {
              access: !isSchoolflat,
            },
          },
          클리닉관: {
            access: true,
          },
          평가관: {
            access: true,
          },
        },
        다른기관학습지: {
          access: !isSchoolflat,
        },
      },
      schoolExamZone: {
        access: !isSchoolflat,
      },
      챗봇: {
        access: !isSchoolflat,
      },
      orderManage: {
        access: true,
      },
      notification: {
        access: true,
      },
    }
  }

  private _checkPermission(
    userAuthority: res.Teacher['type'],
    target?: res.Teacher['type'],
  ): boolean {
    if (!target) return false
    return userAuthority === target
  }
}

const defaultRoutePermission: RoutePermissionType = {
  myinfo: {
    access: false,
    기관정보: { access: false },
    사이언스플랫: { access: false },
    잉글리시플랫: { access: false },
    이용현황: { access: false },
    교재비정산: { access: false },
    사용가이드: { access: false },
    교재구매내역: { access: false },
  },
  // 운영 관리
  manage: {
    access: false,
    student: {
      등록: false,
      정보수정: false,
      삭제: false,
    },
    // 반 관리
    class: {
      access: false,
      myClassOnly: false,
      등록: false,
      삭제: false,
    },
    // 선생님 관리
    teacher: {
      access: false,
      등록: false,
      정보수정: false,
    },
    studentApp: {
      access: false,
    },
    parentApp: {
      access: false,
    },
    // 문자 관리
    sms: {
      access: false,
      보내기: false,
      내역: false,
    },
    // 출결 관리
    attendance: {
      access: false,
    },
    tuition: {
      access: false,
    },
  },
  고객지원: {
    access: false,
  },
  VOD_칼럼: {
    access: false,
  },
  // 수업 준비
  prepare: {
    access: false,
    학습지만들기: {
      학교별기출: { access: false },
      기출업로드: { access: false },
    },
    교재만들기: {
      학교별기출: { access: false },
    },
    학습지: {
      access: false,
      내학습지: {
        access: false,
      },
      나의DB: {
        access: false,
      },
      즐겨찾는문제: {
        access: false,
      },
      학습지휴지통: {
        access: false,
      },
    },
    내교재: {
      access: false,
    },
    학교별기출: {
      access: false,
    },
    강의: {
      access: false,
    },
    KMM: {
      access: false,
    },
    와이즈만: {
      access: false,
    },
    테마별학습지: {
      access: false,
      내신관: {
        access: false,
        내신대비교과서: {
          access: false,
        },
        내신대비추천: {
          access: false,
        },

        내신대비서술형: {
          access: false,
        },
      },
      수능경시관: {
        access: false,
        수능모의고사: {
          access: false,
        },
        모의고사쌍둥이: {
          access: false,
        },
        수능특강수능완성: {
          access: false,
        },
        경찰대사관학교: {
          access: false,
        },
        경찰대사관학교쌍둥이: {
          access: false,
        },
        KMM: {
          access: false,
        },
      },
      클리닉관: {
        access: false,
      },
      평가관: {
        access: false,
      },
    },
    다른기관학습지: {
      access: false,
    },
  },
  lesson: {
    access: false,
  },
  schoolExamZone: {
    access: false,
  },
  챗봇: {
    access: false,
  },
  orderManage: {
    access: false,
  },
  notification: {
    access: false,
  },
}

const seoltabRoutePermission: RoutePermissionType = {
  ...defaultRoutePermission,
  prepare: {
    ...defaultRoutePermission.prepare,
    access: true,
    학습지만들기: {
      학교별기출: { access: true },
      기출업로드: { access: true },
    },
    학습지: {
      access: true,
      내학습지: {
        access: true,
      },
      나의DB: {
        access: false,
      },
      즐겨찾는문제: {
        access: true,
      },
      학습지휴지통: {
        access: true,
      },
    },
    내교재: {
      access: false,
    },
    학교별기출: {
      access: true,
    },
    강의: {
      access: false,
    },
    KMM: {
      access: false,
    },
    와이즈만: {
      access: false,
    },
    다른기관학습지: {
      access: false,
    },
    테마별학습지: {
      ...defaultRoutePermission.prepare.테마별학습지,
      access: true,
      내신관: {
        access: true,
        내신대비교과서: {
          access: true,
        },
        내신대비추천: {
          access: true,
        },
        내신대비서술형: {
          access: false,
        },
      },
      수능경시관: {
        access: true,
        수능모의고사: {
          access: true,
        },
        모의고사쌍둥이: {
          access: true,
        },
        수능특강수능완성: {
          access: true,
        },
        경찰대사관학교: {
          access: true,
        },
        경찰대사관학교쌍둥이: {
          access: true,
        },
        KMM: {
          access: false,
        },
      },
    },
  },
}
